import { useEffect, useState } from "react";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./auth";

export type TicketState = {
  unredeemedCount: number;
  loading: boolean;
  setOptimistic: (count: number) => void;
};

/** Subscribe en tiempo real al count de tickets no canjeados del user en su tenant.
 * Permite override optimista via setOptimistic (cuando una callable retorna el nuevo count
 * antes de que el snapshot listener lo detecte). El snapshot luego sobreescribe con la verdad.
 */
export function useTickets(): TicketState {
  const { user, claims } = useAuth();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !claims.tenantId) {
      setCount(0);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "tenants", claims.tenantId, "tickets"),
      where("userId", "==", user.uid),
      where("redeemed", "==", false)
    );
    return onSnapshot(
      q,
      (snap) => {
        setCount(snap.size);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [user, claims.tenantId]);

  return { unredeemedCount: count, loading, setOptimistic: setCount };
}

export type PurchaseRow = {
  id: string;
  amount: number | null;
  status: string;
  createdAt: Date | null;
};

export function usePurchaseHistory(maxItems = 5): {
  purchases: PurchaseRow[];
  loading: boolean;
} {
  const { user, claims } = useAuth();
  const [state, setState] = useState<{ purchases: PurchaseRow[]; loading: boolean }>({
    purchases: [],
    loading: true
  });

  useEffect(() => {
    if (!user || !claims.tenantId) {
      setState({ purchases: [], loading: false });
      return;
    }
    const q = query(
      collection(db, "tenants", claims.tenantId, "purchases"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc"),
      limit(maxItems)
    );
    return onSnapshot(
      q,
      (snap) => {
        const rows: PurchaseRow[] = snap.docs.map((d) => {
          const x = d.data();
          return {
            id: d.id,
            amount: typeof x.amount === "number" ? x.amount : null,
            status: x.status ?? "unknown",
            createdAt: x.createdAt?.toDate?.() ?? null
          };
        });
        setState({ purchases: rows, loading: false });
      },
      () => setState({ purchases: [], loading: false })
    );
  }, [user, claims.tenantId, maxItems]);

  return state;
}

/** Loyalty config en tiempo real (ticketsForReward). */
export function useLoyaltyConfig(): { ticketsForReward: number; rewardLabel: string; loading: boolean } {
  const { claims } = useAuth();
  const [state, setState] = useState({ ticketsForReward: 5, rewardLabel: "Tu reward", loading: true });

  useEffect(() => {
    if (!claims.tenantId) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    const ref = doc(db, "tenants", claims.tenantId, "config", "loyalty");
    return onSnapshot(
      ref,
      (snap) => {
        const d = snap.data();
        setState({
          ticketsForReward: (d?.ticketsForReward as number | undefined) ?? 5,
          rewardLabel: (d?.rewardLabel as string | undefined) ?? "Tu reward",
          loading: false
        });
      },
      () => setState((s) => ({ ...s, loading: false }))
    );
  }, [claims.tenantId]);

  return state;
}
